import { left, right } from 'fp-ts/Either';
import { BaseApi } from '../../../../hexagon/infra/BaseApi';
import { API_OFFICE_URL } from '../../../../config';
import { ApiResponse } from '../../../../hexagon/infra/ApiResponse';
import { UserDataGateway } from '../../../../hexagon/gateways/userDataGateway.interface';
import { UserFeeds } from '../../../../hexagon/models/userFeedsModel';
import { OfficeCountry, OfficeLanguage } from '../../../../hexagon/config/appConfig';
import { UserStats } from '../../../../hexagon/models/userStatsModel';
import { Banner } from '../../../../hexagon/models/BannerModel';
import { Popup } from '../../../../hexagon/models/PopupModel';
import { UpdatableUserAlcopaCredential } from '../../../../hexagon/models/userModel';

export class AutobizOfficeApiUserDataGateway extends BaseApi implements UserDataGateway {
    setToken(token: string): ApiResponse<boolean> {
        if (token) {
            this.setBearerToken(token);
            return right(true);
        }

        return left({ code: '401', message: 'not authorized' });
    }

    async getUserFeeds(language: OfficeLanguage): Promise<ApiResponse<UserFeeds>> {
        try {
            const response = await this.get(`${API_OFFICE_URL}/userFeeds`, {
                params: {
                    language,
                },
            });
            return right(response.data);
        } catch (error) {
            return left({ code: '401', message: 'retrieve_feeds_failed' });
        }
    }

    async getUserStats(): Promise<ApiResponse<UserStats>> {
        try {
            const response = await this.get(`${API_OFFICE_URL}/userStats`, {});
            return right(response.data);
        } catch (error) {
            return left({ code: '401', message: 'retrieve_stats_failed' });
        }
    }

    async getBanner(
        language: OfficeLanguage,
        country: OfficeCountry,
    ): Promise<ApiResponse<Banner>> {
        try {
            const response = await this.get(
                `${API_OFFICE_URL}/cms/banner?locale=${language.toLowerCase()}&app=autobizOffice&country=${country.toLowerCase()}`,
                {},
            );
            return right(response.data);
        } catch (error) {
            return left({ code: '401', message: 'retrieve_banner_failed' });
        }
    }

    async getPopup(language: OfficeLanguage, country: OfficeCountry): Promise<ApiResponse<Popup>> {
        try {
            const response = await this.get(
                `${API_OFFICE_URL}/cms/popup?locale=${language.toLowerCase()}&app=autobizOffice&country=${country.toLowerCase()}`,
                {},
            );
            return right(response.data);
        } catch (error) {
            return left({ code: '401', message: 'retrieve_popup_failed' });
        }
    }

    async updateUserAlcopa(
        userAlcopa: UpdatableUserAlcopaCredential,
    ): Promise<ApiResponse<boolean>> {
        const { username, password, autobizUserId } = userAlcopa;
        let data = {};
        if (username && password) {
            data = {
                username,
                password,
            };
        }

        try {
            const response = await this.put(
                `${API_OFFICE_URL}/userAlcopa/${autobizUserId
                    .toUpperCase()
                    .replace(/^([A-Z]{2})([0-9]+)$/, '$1_$2')}/credential`,
                { data },
            );
            if (response.status === 200) {
                return right(true);
            }
            return left({ code: 'error_code', message: 'error_message' });
        } catch (error) {
            return left({ code: 'error_code', message: 'error_message' });
        }
    }
}
